import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoginModalType, PostAuthOptions } from 'types';
import type { Image as ImageType } from 'types/capi/shared';

import MailIcon from '~/assets/icons/Mail';
import SignUpMethodEvent from '~/events/SignUpMethodEvent';
import { useRegistrationMethod } from '~/hooks/useRegistrationInteraction';
import useRootData from '~/hooks/useRootData';
import { isInAppBrowser } from '~/utils/browser';

import { Button } from '../../Button';
import Image, { smallImageOptions } from '../../Image';
import LoginButtons from '../../LoginButtons';
import Tracked from '../../Tracked';
import ModalNav from '../ModalNav';
import { authButtonClassName } from './AuthDialog';
import AuthPanel from './AuthPanel';
import ErrorBanner from './ErrorBanner';

function getTitle(modalType: LoginModalType, t: TFunction<'common'>) {
  return {
    account: t('login.stageInitial.profilePage.title'),
    notifications: t('login.stageInitial.notificationAttempt.title'),
    pdpGate: t('login.stageInitial.pdpGate.title'),
    profile: t('login.stageInitial.profilePage.title'),
    purchase: t('login.stageInitial.purchase.title'),
    raffle: t('login.stageInitial.raffle.title')
  }[modalType];
}

type Props = {
  image?: ImageType;
  modalType: LoginModalType;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  onLogInClick: () => void;
  onSignUpClick: () => void;
  postAuthOptions?: PostAuthOptions;
  tempContext?: string;
};

export default function InitialScreen({
  onLogInClick,
  onSignUpClick,
  onDismiss,
  postAuthOptions,
  image,
  modalType,
  tempContext
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('common');
  const { isBot } = useRootData();
  const recordMethod = useRegistrationMethod();

  const isInApp = isInAppBrowser(navigator.userAgent);

  function handleSignUpClick() {
    new SignUpMethodEvent({
      buttonName: 'SIGN_UP_EMAIL_PROMPT',
      isBot,
      pagePosition: 'Sign in modal',
      tempContext
    }).send();
    recordMethod('email');

    onSignUpClick();
  }

  function handleLogInClick() {
    recordMethod('existing_account');

    onLogInClick();
  }

  const socialButtonProps = {
    onError: setError,
    onSuccess: () => onDismiss({ loginSuccessful: true }),
    pagePosition: 'Sign in modal',
    postAuthOptions
  } as const;

  return (
    <>
      <ModalNav
        heading={getTitle(modalType, t)}
        onDismissHref={modalType === 'profile' ? '/' : undefined}
      />
      {image && (
        <Image
          className="aspect-video w-20 object-cover md:w-[182px]"
          loading="eager"
          src={image.url}
          alt={image.alt}
          {...smallImageOptions({ pad: true })}
        />
      )}
      <AuthPanel>
        {!isInApp && (
          <>
            <LoginButtons.Apple appearance="large" {...socialButtonProps} />
            <LoginButtons.Google appearance="large" {...socialButtonProps} />
          </>
        )}
        <Button
          className={authButtonClassName}
          variant="forest"
          onClick={handleSignUpClick}
        >
          <MailIcon height="16" width="16" stroke="white" />
          {t('login.stageInitial.emailSignUpButton')}
        </Button>
        <Tracked.Button
          className={authButtonClassName}
          onClick={handleLogInClick}
          variant="white"
          name="EXISTING_USER_LOGIN_PROMPT"
          pagePosition={'Sign in modal'}
        >
          <MailIcon height="16" width="16" stroke="black" />
          {t('login.stageInitial.emailLoginButton')}
        </Tracked.Button>
        {!isInApp && (
          <LoginButtons.Facebook appearance="link" {...socialButtonProps} />
        )}
      </AuthPanel>

      {error && <ErrorBanner error={error} />}
    </>
  );
}
